import React, { useEffect, useState } from 'react'
import { Button, Form, Table } from 'react-bootstrap'
import { ReactComponent as ArrowLeft } from "../../images/arrow_left.svg"
import { useDataContext } from './CIContext';
import { UpdateCIConfigPayload } from './DataModels';
import displayNotification from '../../notification/displayNotification';
import { NotificationType } from '../../notification/notifications.model';
import { FetchCiConfig, UpdateCiConfig } from './CredibilityIndex.api';
import Logger from '../../utils/Logger';
import { CredibilityIndexAction } from './CredibilityIndex.action';
import { PageViewType } from './CredibilityIndex.constants';
import { toast } from "react-toastify"
import { handlePasteNumeric, isAnyToastVisible, preventInvalidNumberInput } from './ciUtils';

interface BandConfig {
    startValue: number;
    endValue: number;
    bandScore: number;
}

interface EditedConfig {
    weightage: number;
    configuration:  Partial<Record<Band, BandConfig>>;
}

const BANDS = ['none', 'veryLow', 'low', 'medium', 'high'] as const;
type Band = typeof BANDS[number];
interface BandConfig { startValue: number; endValue: number; bandScore: number; }
interface BandItem { name: Band; config: BandConfig; }

const EditBandScore = () => {

    const context = useDataContext();
    const { state: {editCredibilityBand, clientId, getCiConfigResponse, isLoading}, dispatch } = context!;

    const { showNotification } = displayNotification()
 
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    const [editedConfig, setEditedConfig] = useState<EditedConfig>({
        weightage: editCredibilityBand.weightage || 0,
        configuration: {} as Partial<Record<Band, BandConfig>>
    });

    useEffect(() => {
        return () => {
            if (isAnyToastVisible()) {
                toast.dismiss();
            }
        };
    }, [])

    let bands: BandItem[] = [];

    const handleBackButton = () => {
        
        const payload = {
            clientId: clientId
        }
        FetchCiConfig(payload).then((response: any) => {

            dispatch({
                type: CredibilityIndexAction.SET_CI_CONFIG_RESPONSE,
                payload: {
                    response: response,
                    activePage: PageViewType.TABLE_PAGE
                }
            });
        }).catch((error) => {
            Logger.error("Error:", error);
        })
        if (isAnyToastVisible()) {
            toast.dismiss()
        }
    }
 
    const hasFieldError = (band: Band, field: string) => {
        return validationErrors[band]?.includes(field) || false;
    };
 
    const updateConfigValue = (band: Band, field: 'startValue' | 'endValue' | 'bandScore', value: string) => {
        const numericValue = value === '' ? '' : (isNaN(Number(value)) ? value : Number(value));
        setEditedConfig(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                [band]: {
                    ...editCredibilityBand.flag.configuration[band],
                    ...prev.configuration[band],
                    [field]: numericValue
                }
            }
        }));
 
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors[band]) {

                newErrors[band] = newErrors[band].filter(errorField => errorField !== field);
               
                if (newErrors[band].length === 0) {
                    delete newErrors[band];
                }
            }
            return newErrors;
        });
    };
 
    const validateBandSequence = () => {
        const errors: Record<string, string[]> = {};
 
        bands = BANDS
            .map(band => {
                const config = editedConfig.configuration[band] || editCredibilityBand.flag.configuration[band];
                 
                return {
                    name: band,
                    config: config
                };
            })

        const addError = (bandName: string, field: string) => {
      
            if (!errors[bandName]) {
                errors[bandName] = [];
            }
            if (!errors[bandName].includes(field)) {
                errors[bandName].push(field);
            }
        };
 
        let endValue = bands[0]?.config.endValue;
 
        for (let i = 1; i < bands.length; i++) {
            const currentBand = bands[i];
            const previousBand = bands[i - 1];
            const startValue = currentBand.config.startValue;

            const isSpecialCase = (currentBand.name === "none" || currentBand.name === "veryLow") &&
                startValue === 0 && endValue === 0;

            if (isSpecialCase && startValue === endValue) {
                endValue = currentBand.config.endValue;
                continue;
            }

            if ((endValue + 1) !== startValue) {
                addError(previousBand.name, 'endValue');
                addError(currentBand.name, 'startValue');
                setValidationErrors(errors);
                return {
                    isValid: false,
                    error: "Band Values should be in sequence."
                };
            }


            if (startValue > currentBand.config.endValue) {
                addError(currentBand.name, 'startValue');
                setValidationErrors(errors);
                return {
                    isValid: false,
                    error: "Start value of a band must not be greater than end value."
                };
            }

            endValue = currentBand.config.endValue;

        }
        setValidationErrors(errors);
        return {
            isValid: true,
            error: null
        };
    };
 
    const handleSave = () => {
        const validation = validateBandSequence();
 
        if (!validation?.isValid) {
            showNotification(NotificationType.BAND_VALUE_VALIDATION_ERROR, validation.error!);
            return;
        }

        dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: true } })
 
        const configuration = Object.fromEntries(bands.map((b: any) => [b.name, b.config]));
 
        const payload: UpdateCIConfigPayload = {
            clientId: clientId,
            flagConfiguration: {
                ...getCiConfigResponse?.flagConfig,
                [editCredibilityBand.responseKey]: {
                    weightage: editedConfig.weightage,
                    configuration: configuration
                }
            },
            credibilityScoreBand: null,
            exceptionAndEvents: null,
            publicReportShow: null
        };
 
        UpdateCiConfig(payload).then(() => {  
            showNotification(NotificationType.BAND_UPDATE_SUCCESS, "Success! Ci weightage values are saved");
            dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } })
         }).catch((err) => {
            Logger.error("Error:", err);
            dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } })
         })
 
    };
 


    return (
        <div>
            <div className='d-flex mt-4 mb-3'>
                <span className="c-pointer" onClick={handleBackButton}><ArrowLeft /></span>
                <h4 className='nav-padding'>{`Edit Credibility band configuration: "${clientId}"`}</h4>
            </div>

            <div className="d-flex">
                <Form.Control
                    type="text"
                    value={editCredibilityBand.displayName}
                    className='cursor-not-allowed w-25 mr-3'
                    title={editCredibilityBand.displayName}
                    readOnly
                />
                <Form.Control
                    type="number"
                    value={editCredibilityBand.weightage}
                    title={editCredibilityBand.weightage}
                    className='cursor-not-allowed w-25 mr-3'
                    readOnly
                />
                <Button size="sm" className="ml-2" onClick={handleSave} disabled={isLoading}>Save {isLoading && (
                    <span
                        className="spinner-border spinner-border-sm ml-2"
                        role="status"
                        aria-hidden="true"
                    />
                )}</Button>
            </div>
            <div className="mt-4">
                <Table bordered responsive className="text-nowrap">
                    <thead className="bg-sky-lighter">
                        <tr>
                            <th>Band</th>
                            <th>Start Value</th>
                            <th>End Value</th>
                            <th>Band Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {editCredibilityBand.flag && editCredibilityBand.flag.configuration &&
                            BANDS.map((band) => {
                                    const bandConfig = editedConfig.configuration[band] || editCredibilityBand.flag.configuration[band];
                                    return (
                                        <tr key={band}>
                                            <td>{band}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    value={band === 'none' ? 0 : bandConfig.startValue}
                                                    onChange={(e) => updateConfigValue(band, 'startValue', e.target.value)}
                                                    className={`input-width ${hasFieldError(band, 'startValue') ? 'border-danger' : ''} ${band === 'none' ? 'cursor-not-allowed' : ''}`}
                                                    disabled={band === 'none'}
                                                    onKeyDown={preventInvalidNumberInput}
                                                    onPaste={handlePasteNumeric}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type={band === 'high' ? 'text' : 'number'}
                                                    value={band === 'high' ? 'MAX' : bandConfig.endValue}
                                                    onChange={(e) => updateConfigValue(band, 'endValue', e.target.value)}
                                                    className={`input-width ${hasFieldError(band, 'endValue') ? 'border-danger' : ''} ${band === 'high' ? 'cursor-not-allowed' : ''}`}
                                                    disabled={band === 'high'}
                                                    onKeyDown={preventInvalidNumberInput}
                                                    onPaste={handlePasteNumeric}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    value={bandConfig.bandScore}
                                                    onChange={(e) => updateConfigValue(band, 'bandScore', e.target.value)}
                                                    className={"input-width " + (hasFieldError(band, 'bandScore') ? 'border-danger' : '')}
                                                    onKeyDown={preventInvalidNumberInput}
                                                    onPaste={handlePasteNumeric}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                </Table>
            </div>
        </div>
    )
}

export default EditBandScore